import { Box, Stack } from '@mui/material';
import { getRequestEvaluations } from 'lib/api/decisionEngine';
import { getRecieverFactoringRequestOverview } from 'lib/api/factoring';
import {
  BackBubbleButton,
  ContentCard,
  ErrorState,
  getStatusChipColor,
  HeroHeader,
  IconTitleCard,
  IconTitleCardSkeleton,
  MobileBody,
  StatusChip,
} from 'lib/mobileDesignSystem';
import type { Evaluation } from 'lib/types/models';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const NODES_SKELETON_COUNT = 3;

const REQUEST_TYPES = [
  'factoring-request',
  'factoring-request-payers',
] as const;

function getStatusColor(
  statusName: string,
): 'success' | 'error' | 'warning' | 'default' {
  switch (statusName) {
    case 'Aprobado':
      return 'success';
    case 'Rechazado':
      return 'error';
    default:
      return 'warning';
  }
}

interface LocationState {
  evaluation?: Evaluation;
  quoteId?: number;
}

const EvaluationNodesPage: React.FC = () => {
  const { id, evaluationId } = useParams<{
    id: string;
    evaluationId: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const state = location.state as LocationState | undefined;

  const { data: overview } = getRecieverFactoringRequestOverview(dispatch, {
    requestId: id ?? '',
  });
  const quoteIdFromOverview = overview?.quoteId ?? null;
  const quoteId = state?.quoteId ?? quoteIdFromOverview ?? null;
  const requestIdNum = id != null && id !== '' ? Number(id) : null;

  const evaluationsQueries = [
    getRequestEvaluations(dispatch, {
      requestType: REQUEST_TYPES[0],
      requestId: quoteId,
    }),
    getRequestEvaluations(dispatch, {
      requestType: REQUEST_TYPES[1],
      requestId: requestIdNum,
    }),
  ];

  const allEvaluations = useMemo(
    () => evaluationsQueries.flatMap((q) => Object.values(q.data ?? {})).flat(),
    [evaluationsQueries[0].data, evaluationsQueries[1].data],
  );

  const evaluationFromState = state?.evaluation;
  const evaluationFromApi = useMemo(() => {
    if (evaluationFromState != null) return null;
    const eid = evaluationId != null ? Number(evaluationId) : NaN;
    if (Number.isNaN(eid)) return null;
    return allEvaluations.find((e) => e.id === eid) ?? null;
  }, [evaluationFromState, evaluationId, allEvaluations]);

  const evaluation = evaluationFromState ?? evaluationFromApi;
  const isResolvingFromApi =
    evaluationFromState == null &&
    evaluationFromApi == null &&
    (evaluationsQueries[0].isPending ||
      evaluationsQueries[1].isPending ||
      (overview === undefined && !!id));

  const nodes = evaluation?.result ?? [];
  const hasNodes = nodes.length > 0;
  const title = evaluation?.label ?? 'Nodos de evaluación';

  const handleBack = () => navigate(-1);

  if (!id) {
    return <ErrorState message="Identificador de operación no válido." />;
  }

  if (evaluationFromState == null && overview != null && quoteId == null) {
    return (
      <ErrorState message="No se encontró cotización para esta operación." />
    );
  }

  if (
    evaluationFromState == null &&
    !evaluationsQueries[0].isPending &&
    !evaluationsQueries[1].isPending &&
    evaluation == null &&
    overview !== undefined
  ) {
    return (
      <ErrorState message="No se encontró la evaluación o no tiene nodos." />
    );
  }

  return (
    <Stack sx={{ minHeight: '100%', pb: 4, px: 0 }} spacing={0}>
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <HeroHeader
          title={title}
          minHeight={60}
          fadeColor={getStatusChipColor('info')}
          sx={{ width: '100vw', mx: 'calc(-50vw + 50%)' }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            pt: 0.5,
            px: 2,
          }}
        >
          <BackBubbleButton onClick={handleBack} />
        </Box>
      </Box>

      <Stack spacing={2} sx={{ px: 2, pt: 2, flex: 1 }}>
        {isResolvingFromApi ? (
          <Stack spacing={2}>
            {Array.from({ length: NODES_SKELETON_COUNT }, (_, i) => (
              <ContentCard key={`skeleton-${i}`}>
                <Stack spacing={1.5}>
                  <IconTitleCardSkeleton />
                </Stack>
              </ContentCard>
            ))}
          </Stack>
        ) : evaluation == null ? (
          <MobileBody color="text.secondary">
            No se pudo cargar la evaluación.
          </MobileBody>
        ) : !hasNodes ? (
          <MobileBody color="text.secondary">
            No hay nodos para esta evaluación.
          </MobileBody>
        ) : (
          <Stack spacing={2}>
            {nodes.map((node, nodeIndex) => (
              <ContentCard key={`${node.nodeIdDecision}-${nodeIndex}`}>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <StatusChip
                      text={node.statusNode.name}
                      type={getStatusColor(node.statusNode.name)}
                    />
                  </Box>
                  <Box>
                    <IconTitleCard
                      adornmentText={nodeIndex + 1}
                      title={node.nodeName || 'Sin comentarios'}
                      description={node.resultText || undefined}
                      sx={{ px: 1.5, py: 1.25 }}
                    />
                  </Box>
                </Stack>
              </ContentCard>
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default EvaluationNodesPage;
